import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import Header from '../components/Header';

interface Address {
  id: string;
  label: string;
  address: string;
  landmark?: string;
  latitude: number;
  longitude: number;
  isDefault: boolean;
}

const defaultCenter = {
  lat: 28.6139,  // Default to Delhi coordinates
  lng: 77.2090
};

const containerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '0.75rem',
  position: 'relative' as const
};

const centerPinStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '2rem',
  color: '#2563eb',
  zIndex: 1,
  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

const currentLocationButtonStyle = {
  position: 'absolute' as const,
  bottom: '24px',
  right: '10px',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '8px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  zIndex: 1,
  border: '1px solid rgba(0,0,0,0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

const AddressPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAtwg6QhkUq9n10t6er1W_D9qSa4aUXPoo"
  });
  const [addresses, setAddresses] = useState<Address[]>(() => {
    const savedAddresses = localStorage.getItem('minto_addresses');
    return savedAddresses ? JSON.parse(savedAddresses) : [];
  });
  const [selectedLocation, setSelectedLocation] = useState(defaultCenter);
  const [newAddress, setNewAddress] = useState({
    label: '',
    address: '',
    landmark: '',
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    isDefault: false
  });
  const [isAddingNew, setIsAddingNew] = useState(false);

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setSelectedLocation(latLng);
          if (map) {
            map.panTo(latLng);
          }
          // Reverse geocode to get address
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: latLng }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              setNewAddress(prev => ({
                ...prev,
                address: results[0].formatted_address,
                latitude: latLng.lat,
                longitude: latLng.lng
              }));
            }
          });
        },
        () => {
          console.log('Location access denied');
        }
      );
    }
  };

  useEffect(() => {
    // Get user's current location on mount
    getCurrentLocation();
  }, []);

  useEffect(() => {
    localStorage.setItem('minto_addresses', JSON.stringify(addresses));
  }, [addresses]);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setSelectedLocation({ lat, lng });

      // Reverse geocode to get address
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          setNewAddress(prev => ({
            ...prev,
            address: results[0].formatted_address,
            latitude: lat,
            longitude: lng
          }));
        }
      });
    }
  };

  const handleAddAddress = () => {
    const newAddressItem: Address = {
      id: Date.now().toString(),
      ...newAddress
    };

    setAddresses(prev => {
      // If this is the first address or marked as default, update other addresses
      if (newAddressItem.isDefault || prev.length === 0) {
        return [...prev.map(addr => ({ ...addr, isDefault: false })), { ...newAddressItem, isDefault: true }];
      }
      return [...prev, newAddressItem];
    });

    setIsAddingNew(false);
    setNewAddress({
      label: '',
      address: '',
      landmark: '',
      latitude: defaultCenter.lat,
      longitude: defaultCenter.lng,
      isDefault: false
    });
  };

  const handleSetDefault = (addressId: string) => {
    setAddresses(prev =>
      prev.map(addr => ({
        ...addr,
        isDefault: addr.id === addressId
      }))
    );
  };

  const handleDeleteAddress = (addressId: string) => {
    setAddresses(prev => prev.filter(addr => addr.id !== addressId));
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Manage Addresses" />

      <div className="container mx-auto px-4 py-6 pb-28">
        {/* Map Section */}
        <div className="mb-6 relative">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={selectedLocation}
            zoom={15}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onClick={handleMapClick}
            options={{
              zoomControl: true,
              mapTypeControl: false,
              scaleControl: false,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: false
            }}
          >
            {/* Center Pin Icon */}
            <div style={centerPinStyle}>
              <i className="fas fa-map-marker-alt"></i>
            </div>

            {/* Current Location Button */}
            <button
              onClick={getCurrentLocation}
              style={currentLocationButtonStyle}
              className="hover:bg-gray-50 active:bg-gray-100"
            >
              <i className="fas fa-crosshairs text-blue-600"></i>
              <span className="text-sm font-medium text-gray-700">Current Location</span>
            </button>
          </GoogleMap>
        </div>

        {/* Address List */}
        <div className="space-y-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className="bg-white rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{address.label}</h3>
                    {address.isDefault && (
                      <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{address.address}</p>
                  {address.landmark && (
                    <p className="text-sm text-gray-500 mt-1">Landmark: {address.landmark}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!address.isDefault && (
                    <button
                      onClick={() => handleSetDefault(address.id)}
                      className="text-sm text-minto-600 hover:text-minto-700"
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteAddress(address.id)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add New Address Form */}
        {isAddingNew ? (
          <div className="mt-6 bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-medium text-gray-900 mb-4">Add New Address</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label
                </label>
                <input
                  type="text"
                  placeholder="e.g., Home, Office"
                  value={newAddress.label}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-minto-500/20 focus:border-minto-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  placeholder="Full address"
                  value={newAddress.address}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-minto-500/20 focus:border-minto-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Landmark (Optional)
                </label>
                <input
                  type="text"
                  placeholder="Any nearby landmark"
                  value={newAddress.landmark}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, landmark: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-minto-500/20 focus:border-minto-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={newAddress.isDefault}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="h-4 w-4 text-minto-600 focus:ring-minto-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                  Set as default address
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleAddAddress}
                  className="flex-1 px-4 py-2 bg-minto-500 text-white rounded-lg hover:bg-minto-600 active:bg-minto-700 transition-colors"
                >
                  Save Address
                </button>
                <button
                  onClick={() => setIsAddingNew(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsAddingNew(true)}
            className="mt-6 w-full px-4 py-3 bg-minto-500 text-white rounded-xl hover:bg-minto-600 active:bg-minto-700 transition-colors flex items-center justify-center gap-2"
          >
            <i className="fas fa-plus"></i>
            Add New Address
          </button>
        )}
      </div>
    </div>
  );
};

export default AddressPage; 